import React, {useContext, useEffect, useRef, useState} from "react"
import Row from "@amzn/meridian/row"
import Column from "@amzn/meridian/column"
import {TableCell, TableRow} from "@amzn/meridian/table";
import {FormattedMessage, injectIntl} from "react-intl";
import {getAssetRefreshInterval, sortComparator} from "../../util/Util";
import {chainWalk} from "../../util/Util";
import Box from "@amzn/meridian/box";
import Button from "@amzn/meridian/button";
import Icon from "@amzn/meridian/icon";
import AddAslGuidedDivertConfigurationModal from "./modal/AddAslGuidedDivertConfigurationModal";
import {AslDeviceOverviewContext} from "./context/AslDeviceOverviewContext";
import Loader from "../../loader/Loader";
import DeleteAslGuidedDivertConfigurationModal from "./modal/DeleteAslGuidedDivertConfigurationModal";
import plusCircleTokens from "@amzn/meridian-tokens/base/icon/plus-circle";
import trashTokens from "@amzn/meridian-tokens/base/icon/trash";
import ConfigurationTabHeader from "../../commonui/ConfigurationTabHeader";
import {
    ASCENDING, ASL_ASSET_TYPE, ASSET_CONFIG_ATTRIBUTES, CONFIG_TYPE, OPERATION_COLOR, SEARCH_ROW_COLOR
} from "../../util/Constants";
import {DraftContext} from "../../draftSave/context/DraftSaveContext";
import PaginatedTable from "../../commonui/PaginatedTable";
import Text from "@amzn/meridian/text";
import arrowRightTokens from "@amzn/meridian-tokens/base/icon/arrow-right-large";
import {getAssetByVersion, getAssetVersions} from "../../resources/AssetResource";
import RollbackModal from "../../rollback/RollbackModal";
import {PermissionsContext} from "../../permissions/PermissionsContext";
import {getLocalizedMessage} from "../../util/Localization";
import {ConditionalWrapping, tooltipWrapper} from "../../util/ConditionalWrapping";
import Input from "@amzn/meridian/input";
import uploadTokens from "@amzn/meridian-tokens/base/icon/upload-small";
import UploadFileModal from "../../commonui/UploadFileModal";
import {AslInductTestPackageCSVParser} from "../../util/Parsers";
import {isAslInductTestPackageEnabled} from "../../util/Validations";
import AddAslInductTestPackageModal from "./modal/AddAslInductTestPackageModal";
import DeleteAslInductTestPackageModal from "./modal/DeleteAslInductTestPackageModal";

const ASL_CONFIG_ATTRIBUTES = ASSET_CONFIG_ATTRIBUTES[ASL_ASSET_TYPE];

const getScannablesString = (scannables) => scannables.join(' ');

const AslDeviceConfiguration = ({intl}) => {

    const {viewModel, dispatch} = useContext(AslDeviceOverviewContext);
    const {getDraftData, setDraftData, resetDraftData, getFilteredDraftData, isDataEdited} = useContext(DraftContext);
    const {isAuthorized} = useContext(PermissionsContext);
    const [valueConfiguration, setValueConfiguration] = useState(CONFIG_TYPE.DIVERT_CONFIGURATION)
    const [openAddModal, setOpenAddModal] = useState(false)
    const [openDeleteModal, setOpenDeleteModal] = useState(false)
    const [selectedMac, setSelectedMac] = useState("")
    const [showRollbackModal, setShowRollbackModal] = useState(false)
    const [isVersionsLoading, setIsVersionsLoading] = useState(false);
    const [selectedVersion, setSelectedVersion] = useState("");
    const [rollBackVersions, setRollbackVersions] = useState([]);
    const [guidedDivertConfigColumn, setGuidedDivertConfigColumn] = useState("divertCode");
    const [guidedDivertConfigOrder, setGuidedDivertConfigOrder] = useState(ASCENDING);
    const [openCSVModal, setOpenCSVModal] = useState(false)
    const [testPackageSortColumn, setTestPackageSortColumn] = useState("scannables");
    const [testPackageSortOrder, setTestPackageSortOrder] = useState(ASCENDING);
    const [scannablesSearchInput, setScannablesSearchInput] = useState('');
    const [sortZoneSearchInput, setSortZoneSearchInput] = useState('');
    const tableEndRef = useRef(null)

    // Stations outside the Induct Test Package list see only the divert configuration.
    const isInductTestPackageEnabled = isAslInductTestPackageEnabled();
    const PAGE_CONFIGURATION_KEYS = isInductTestPackageEnabled ?
        [CONFIG_TYPE.DIVERT_CONFIGURATION, CONFIG_TYPE.ASL_INDUCT_TEST_PACKAGE] : [CONFIG_TYPE.DIVERT_CONFIGURATION]

    useEffect(() => {
        if (!openAddModal && !openDeleteModal) {
            getData()
            const interval = setInterval(() => {
                if (!document.hidden) {
                    getData();
                }
            }, getAssetRefreshInterval());
            return () => clearInterval(interval);
        }
    }, [openAddModal, openDeleteModal]);

    const getModalContent = async () => {
        setIsVersionsLoading(true);
        let versionsData = await getAssetVersions(selectedMac);
        setRollbackVersions(versionsData.data.assetVersionDetailsList);
        setIsVersionsLoading(false);
    }

    // Rolls back only the config type on screen; the other one keeps its current draft.
    const loadRollbackDataToDraft = async () => {
        const rollbackData = await getAssetByVersion(selectedMac, selectedVersion);
        const attributeName = valueConfiguration === CONFIG_TYPE.ASL_INDUCT_TEST_PACKAGE ?
            ASL_CONFIG_ATTRIBUTES.INDUCT_TEST_PACKAGE : ASL_CONFIG_ATTRIBUTES.DIVERT_CONFIG;
        const configData = chainWalk(() => JSON.parse(rollbackData.data.asset.assetDetails.assetAttributes[attributeName]), []);
        if(configData.length) {
            setDraftData({
                data: configData,
                stateKey: valueConfiguration,
                subKey: selectedMac,
                allRowsAdded: true,
                override: true
            });
        }
    }

    const getSortedDeviceList = () => {
        const sortedDeviceList = Object.values(viewModel.aslDeviceInformation);
        sortedDeviceList && sortedDeviceList.sort((a, b) => {
            if (a.name < b.name) {
                return -1
            } else if (b.name > a.name) {
                return 1
            }
            return 0
        })
        return sortedDeviceList
    }

    const getData = () => {
        setOpenAddModal(false)
        setOpenDeleteModal(false)
        dispatch({type: 'LOAD_VIEW', data: {dispatcher: dispatch}});
    }
    const scrollToBottom = () => {
        tableEndRef.current.scrollIntoView({behavior: "smooth"})
    }

    function onClickAddSingle() {
        setOpenAddModal(true)
    }

    function onClickDeleteConfiguration() {
        setOpenDeleteModal(true)
    }

    useEffect(() => {
        setDraftData({
            data: chainWalk(() => viewModel.aslDeviceInformation[selectedMac].divertConfig, []),
            stateKey: CONFIG_TYPE.DIVERT_CONFIGURATION,
            subKey: selectedMac,
        })
        if (isInductTestPackageEnabled) {
            setDraftData({
                data: chainWalk(() => viewModel.aslDeviceInformation[selectedMac].inductTestPackage, []),
                stateKey: CONFIG_TYPE.ASL_INDUCT_TEST_PACKAGE,
                subKey: selectedMac,
            })
        }
    }, [selectedMac]);

    const deployDraftASLAsset = () => {
        const divertConfigList = getFilteredDraftData({
            stateKey: CONFIG_TYPE.DIVERT_CONFIGURATION,
            subKey: selectedMac
        })
        const configurations = {divertConfig: divertConfigList};
        if (isInductTestPackageEnabled) {
            configurations.inductTestPackage = getFilteredDraftData({
                stateKey: CONFIG_TYPE.ASL_INDUCT_TEST_PACKAGE,
                subKey: selectedMac
            })
        }
        dispatch({
            type: 'UPDATE_ASL_CONFIGURATION',
            data: {
                dispatcher: dispatch,
                configurations,
                mac: selectedMac
            }
        });
        setDraftData({
            data: divertConfigList,
            stateKey: CONFIG_TYPE.DIVERT_CONFIGURATION,
            subKey: selectedMac,
            override: true
        })
        if (isInductTestPackageEnabled) {
            setDraftData({
                data: configurations.inductTestPackage,
                stateKey: CONFIG_TYPE.ASL_INDUCT_TEST_PACKAGE,
                subKey: selectedMac,
                override: true
            })
        }
    }

    const updateTestPackageDraftData = (inductTestPackage) => {
        setDraftData({
            data: inductTestPackage,
            stateKey: CONFIG_TYPE.ASL_INDUCT_TEST_PACKAGE,
            subKey: selectedMac,
            allRowsAdded: true,
            override: true
        })
    }

    const headerProps = {
        selectedMac,
        setSelectedMac,
        valueConfiguration,
        setValueConfiguration,
        isSorterConfig: valueConfiguration === CONFIG_TYPE.DIVERT_CONFIGURATION, intl,
        resetDraftData,
        deployDraft: deployDraftASLAsset,
        scrollToBottom,
        defaultSelection: CONFIG_TYPE.DIVERT_CONFIGURATION,
        sortedDeviceList: getSortedDeviceList(),
        configurationKeys: PAGE_CONFIGURATION_KEYS,
        refreshDeps: [viewModel.timestamp],
        isDeployDisabled: !isDataEdited(PAGE_CONFIGURATION_KEYS),
        isRollbackDisabled: !selectedMac || isDataEdited(PAGE_CONFIGURATION_KEYS),
        rollbackClickHandler: () => {getModalContent(); setShowRollbackModal(true)}
    }

    const renderTableHeader = () => {
        return (
            <TableRow highlightOnHover={false}>
                <TableCell sortColumn="divertCode">
                    <FormattedMessage id="modal.divertcode" defaultMessage="Divert Code"/>
                </TableCell>
                <TableCell sortColumn="clusters">
                    <FormattedMessage id="slam.clusters" defaultMessage="Clusters"/>
                </TableCell>
            </TableRow>
        )
    }

    const renderTableRow = (config) => {
        const dataString = config.clusters.join(" ");
        const prevDataString = chainWalk(() => config.prev.clusters.join(" "), "")
        return (
            <TableRow highlightOnHover={true} backgroundColor={OPERATION_COLOR[config.operation]}>
                <TableCell>
                    {config.divertCode}
                </TableCell>
                <TableCell>
                    {prevDataString && <Text> {prevDataString} </Text>}
                    {prevDataString && <Icon tokens={arrowRightTokens}/>}
                    <Text> {dataString} </Text>
                </TableCell>
            </TableRow>
        )
    }

    const sortGuidedDivertConfigColumns = {
        divertCode: (a, b, ascending) => {
            return ascending ? sortComparator(a.divertCode, b.divertCode) : -sortComparator(a.divertCode, b.divertCode);
        },
        clusters: (a, b, ascending) => {
            return ascending ? sortComparator(a.clusters.join(" "), b.clusters.join(" ")) : -sortComparator(a.clusters.join(" "), b.clusters.join(" "));
        }
    }

    const sortTestPackageColumns = {
        scannables: (a, b, ascending) => {
            return ascending ? sortComparator(getScannablesString(a.scannables), getScannablesString(b.scannables))
                : -sortComparator(getScannablesString(a.scannables), getScannablesString(b.scannables));
        },
        sortZone: (a, b, ascending) => {
            return ascending ? sortComparator(a.sortZone, b.sortZone) : -sortComparator(a.sortZone, b.sortZone);
        }
    }

    const getTestPackageTableData = () => {
        return getDraftData({stateKey: CONFIG_TYPE.ASL_INDUCT_TEST_PACKAGE, subKey: selectedMac})
            .filter(item => getScannablesString(item.scannables).toLowerCase().includes(scannablesSearchInput.toLowerCase()))
            .filter(item => item.sortZone.toLowerCase().includes(sortZoneSearchInput.toLowerCase()))
            .sort((a, b) => sortTestPackageColumns[testPackageSortColumn](a, b, testPackageSortOrder === ASCENDING));
    }

    const renderTestPackageTableHeader = () => {
        return (
            <TableRow highlightOnHover={false}>
                <TableCell>
                    {getLocalizedMessage({intl, stringId: "bay.index"})}
                </TableCell>
                <TableCell sortColumn="scannables">
                    {getLocalizedMessage({intl, stringId: "bay.scannables"})}
                </TableCell>
                <TableCell sortColumn="sortZone">
                    {getLocalizedMessage({intl, stringId: "bay.sortzone"})}
                </TableCell>
            </TableRow>
        )
    }

    const renderTestPackageTableRow = (config, serialNumber) => {
        const prevSortZone = chainWalk(() => config.prev.sortZone, null)
        return (
            <TableRow highlightOnHover={true} backgroundColor={OPERATION_COLOR[config.operation]}>
                <TableCell>
                    {serialNumber}
                </TableCell>
                <TableCell>
                    {getScannablesString(config.scannables)}
                </TableCell>
                <TableCell>
                    {prevSortZone && <Text> {prevSortZone} </Text>}
                    {prevSortZone && <Icon tokens={arrowRightTokens}/>}
                    <Text> {config.sortZone} </Text>
                </TableCell>
            </TableRow>
        )
    }

    const renderTestPackageSearchRow = () => {
        return (
            <TableRow backgroundColor={SEARCH_ROW_COLOR}>
                <TableCell/>
                <TableCell>
                    <Input
                        className="input-search-class"
                        value={scannablesSearchInput}
                        onChange={setScannablesSearchInput}
                        type="text"
                        placeholder={getLocalizedMessage({intl, stringId: "asltestpackage.search"})}
                        width="70%"
                        size="small"/>
                </TableCell>
                <TableCell>
                    <Input
                        className="input-search-class"
                        value={sortZoneSearchInput}
                        onChange={setSortZoneSearchInput}
                        type="text"
                        placeholder={getLocalizedMessage({intl, stringId: "asltestpackage.search"})}
                        width="70%"
                        size="small"/>
                </TableCell>
            </TableRow>
        )
    }

    if (viewModel.loadingOverview) {
        return (<Loader/>)
    }
    return (
        <Column>
            <ConfigurationTabHeader {...headerProps} isAuthorized={isAuthorized}/>
            <RollbackModal
                showRollbackModal={showRollbackModal}
                setShowRollbackModal={setShowRollbackModal}
                isVersionsLoading={isVersionsLoading}
                selectedVersion={selectedVersion}
                setSelectedVersion={setSelectedVersion}
                rollBackVersions={rollBackVersions}
                loadRollbackDataToDraft={loadRollbackDataToDraft}
                intl={intl}
            />
            {valueConfiguration === CONFIG_TYPE.DIVERT_CONFIGURATION && selectedMac && <Column>
                <PaginatedTable
                    data={getDraftData({stateKey: CONFIG_TYPE.DIVERT_CONFIGURATION, subKey: selectedMac}).sort((a, b) => {
                        return sortGuidedDivertConfigColumns[guidedDivertConfigColumn](a, b, guidedDivertConfigOrder === ASCENDING);
                    })}
                    renderRow={renderTableRow}
                    renderHeader={renderTableHeader}
                    sortColumn={guidedDivertConfigColumn}
                    sortDirection={guidedDivertConfigOrder}
                    onSort={({sortColumn, sortDirection}) => {
                        setGuidedDivertConfigColumn(sortColumn);
                        setGuidedDivertConfigOrder(sortDirection);
                    }}
                />
                <Row width="100%" widths={["100", "fill", "100"]}>
                    <ConditionalWrapping
                        condition={!isAuthorized}
                        wrapper={tooltipWrapper}
                        args={{
                            title: getLocalizedMessage({intl, stringId: "tooltip.isAuthorized"}),
                            position: "end"
                        }}
                        child={<Button onClick={onClickAddSingle} type="primary" size="small"
                                       disabled={!isAuthorized}
                        >
                            <Icon tokens={plusCircleTokens}/>
                            <FormattedMessage id="config.addupdateasl"
                                              defaultMessage="Add/Update Guided Divert Configuration"/>
                        </Button>}
                    />
                    <Box></Box>
                    <ConditionalWrapping
                        condition={!isAuthorized}
                        wrapper={tooltipWrapper}
                        args={{
                            title: getLocalizedMessage({intl, stringId: "tooltip.isAuthorized"}),
                            position: "end"
                        }}
                        child={<Button onClick={onClickDeleteConfiguration} type="tertiary" size="small"
                                       disabled={!isAuthorized}
                        >
                            <Icon tokens={trashTokens}/>
                            <FormattedMessage id="config.delete" defaultMessage="Delete Configuration"/>
                        </Button>}
                    />
                </Row>
                <AddAslGuidedDivertConfigurationModal mac={selectedMac}
                                                      open={openAddModal} setOpen={setOpenAddModal}/>
                <DeleteAslGuidedDivertConfigurationModal mac={selectedMac}
                                                         open={openDeleteModal} setOpen={setOpenDeleteModal}/>
                <div ref={tableEndRef}/>
            </Column>}
            {isInductTestPackageEnabled && valueConfiguration === CONFIG_TYPE.ASL_INDUCT_TEST_PACKAGE && selectedMac &&
            <Column>
                <PaginatedTable
                    data={getTestPackageTableData()}
                    renderRow={renderTestPackageTableRow}
                    renderHeader={renderTestPackageTableHeader}
                    renderSearchRow={renderTestPackageSearchRow}
                    sortColumn={testPackageSortColumn}
                    sortDirection={testPackageSortOrder}
                    onSort={({sortColumn, sortDirection}) => {
                        setTestPackageSortColumn(sortColumn);
                        setTestPackageSortOrder(sortDirection);
                    }}
                />
                {/* Not permission-gated on purpose, like the ADTF and ZancaSort test packages: it is test data only. */}
                <Row width="100%" widths={["100", "100", "fill", "100"]}>
                    <Button id="add-induct-test-package-btn" onClick={onClickAddSingle} type="primary" size="small">
                        <Icon tokens={plusCircleTokens}/>
                        {getLocalizedMessage({intl, stringId: "asltestpackage.addupdate"})}
                    </Button>
                    <Button id="upload-induct-test-package-btn" onClick={() => setOpenCSVModal(true)}
                            type="primary" size="small">
                        <Icon tokens={uploadTokens}/>
                        {getLocalizedMessage({intl, stringId: "asltestpackage.uploadCSV"})}
                    </Button>
                    <Box></Box>
                    <Button id="delete-induct-test-package-btn" onClick={onClickDeleteConfiguration}
                            type="tertiary" size="small">
                        <Icon tokens={trashTokens}/>
                        {getLocalizedMessage({intl, stringId: "asltestpackage.deletetitle"})}
                    </Button>
                </Row>
                <UploadFileModal mac={selectedMac}
                                 open={openCSVModal} setOpen={setOpenCSVModal}
                                 parser={AslInductTestPackageCSVParser} updateConfig={updateTestPackageDraftData}/>
                <AddAslInductTestPackageModal mac={selectedMac}
                                              open={openAddModal} setOpen={setOpenAddModal}/>
                <DeleteAslInductTestPackageModal mac={selectedMac}
                                                 open={openDeleteModal} setOpen={setOpenDeleteModal}/>
                <div ref={tableEndRef}/>
            </Column>}
        </Column>
    );
}


export default injectIntl(AslDeviceConfiguration)



// WEBPACK FOOTER //
// ./src/components/devicemanagement/asldevice/AslDeviceConfiguration.js